// Palette de la page sessions : une seule teinte (le navy de la marque) porte
// les jauges, les couleurs de statut restent réservées à l'état de la session.
const INK = "#151B2F";
const INK_MUTED = "#5B6478";
const NAVY = "#232C48";
const NAVY_TRACK = "#DDE1EA";
const SURFACE = "#FFFFFF";
const SURFACE_ALT = "#F6F8FA";
const BORDER = "#E3E7EE";
const LIVE_INK = "#8A5A00";
const LIVE_BG = "#FFF4D6";
const DONE_INK = "#1E6B37";
const DONE_BG = "#E6F4EA";

const styles = (theme) => ({
  root: {
    flexGrow: 1,
    backgroundColor: "#F6F8FA",
    minHeight: "100vh",
  },
  pannel: {
    backgroundColor: "#F6F8FA",
    minHeight: "calc(100vh - 100px)",
    borderRadius: "12px",
    padding: theme.spacing(3),
  },

  // Header styles
  headerIcon: {
    fontSize: "2.5rem",
    color: theme.palette.primary.main,
    marginRight: theme.spacing(2),
  },

  title: {
    fontWeight: 700,
    color: theme.palette.primary.main,
    fontSize: "2rem",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.5rem",
    },
  },

  tourTitle: {
    fontWeight: 600,
    color: INK,
    fontSize: "1.25rem",
    padding: theme.spacing(1.5),
    backgroundColor: "white",
    borderRadius: "8px",
    border: `2px solid ${theme.palette.secondary.main}`,
    display: "inline-block",
  },

  // Table styles
  tableContainer: {
    backgroundColor: "white",
    borderRadius: "12px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
    overflowX: "auto",
    overflowY: "hidden",
    marginBottom: theme.spacing(3),
  },

  table: {
    minWidth: 650,
  },

  tableHeaderCell: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    fontWeight: 600,
    fontSize: "1rem",
    padding: theme.spacing(2),
    borderBottom: `2px solid ${theme.palette.primary.dark}`,
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.875rem",
      padding: theme.spacing(1),
    },
  },

  tableCell: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
    },
  },

  tableRow: {
    "&:hover": {
      backgroundColor: theme.palette.grey[50],
    },
    transition: "background-color 0.2s ease",
  },

  // Content styles
  teamName: {
    fontWeight: 600,
    color: theme.palette.primary.main,
    maxWidth: "200px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  email: {
    color: theme.palette.text.secondary,
    maxWidth: "250px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    [theme.breakpoints.down("md")]: {
      maxWidth: "150px",
    },
  },

  photoCountChip: {
    backgroundColor: theme.palette.secondary.main,
    color: INK,
    fontWeight: 600,
  },

  photoButton: {
    color: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: "rgba(35, 44, 72, 0.08)",
      color: theme.palette.primary.main,
    },
    "&.Mui-focusVisible": {
      outline: `2px solid ${NAVY}`,
      outlineOffset: 2,
    },
    "&.Mui-disabled": {
      color: theme.palette.grey[400],
    },
  },

  // Sort label styles
  sortLabel: {
    color: "white !important",
    "& .MuiTableSortLabel-icon": {
      color: "white !important",
    },
    "&:hover": {
      color: theme.palette.secondary.main + " !important",
    },
    "&.MuiTableSortLabel-active": {
      color: theme.palette.secondary.main + " !important",
    },
  },

  // Pagination styles
  pagination: {
    backgroundColor: "white",
    borderRadius: "12px",
    boxShadow: "none",
    border: `1px solid ${BORDER}`,
    padding: theme.spacing(1),
  },

  // Carousel dialog styles
  carouselDialog: {
    "& .MuiDialog-paper": {
      borderRadius: "16px",
      maxWidth: "90vw",
      maxHeight: "90vh",
    },
  },

  carouselTitle: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    "& .MuiTypography-root": {
      color: "white",
    },
  },

  carouselContent: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.grey[50],
  },

  // Responsive adjustments
  [theme.breakpoints.down("sm")]: {
    pannel: {
      padding: theme.spacing(2),
    },

    title: {
      fontSize: "1.5rem",
    },

    headerIcon: {
      fontSize: "2rem",
      marginRight: theme.spacing(1),
    },

    tourTitle: {
      fontSize: "1rem",
      padding: theme.spacing(1),
    },
  },

  [theme.breakpoints.down("xs")]: {
    pannel: {
      padding: theme.spacing(1),
    },

    title: {
      fontSize: "1.25rem",
    },

    headerIcon: {
      fontSize: "1.75rem",
    },
  },
  mainContent: {
    marginLeft: 180,
    paddingBottom: 60,
    [theme.breakpoints.down("xs")]: {
      marginLeft: 0,
      marginTop: 80,
    },
  },

  // ---------------------------------------------------------------------------
  // Barre de rafraîchissement (suivi en direct)
  // ---------------------------------------------------------------------------
  refreshBar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "space-between",
    backgroundColor: SURFACE,
    border: `1px solid ${BORDER}`,
    borderRadius: 10,
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(2),
  },

  refreshHint: {
    color: INK_MUTED,
    fontSize: "0.8125rem",
    marginLeft: theme.spacing(1),
  },

  liveCount: {
    display: "flex",
    alignItems: "center",
    fontWeight: 600,
    color: LIVE_INK,
    fontSize: "0.875rem",
  },

  // Point clignotant : signale que la donnée bouge, jamais seul (toujours
  // accompagné d'un libellé).
  liveDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: theme.palette.secondary.main,
    marginRight: theme.spacing(1),
    animation: "$pulse 1.6s ease-in-out infinite",
    "@media (prefers-reduced-motion: reduce)": {
      animation: "none",
    },
  },

  "@keyframes pulse": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.25 },
  },

  // ---------------------------------------------------------------------------
  // Badges de statut
  // ---------------------------------------------------------------------------
  statusChip: {
    fontWeight: 600,
    fontSize: "0.75rem",
    height: 24,
  },

  statusChipLive: {
    backgroundColor: LIVE_BG,
    color: LIVE_INK,
  },

  statusChipDone: {
    backgroundColor: DONE_BG,
    color: DONE_INK,
  },

  // Le contenu du badge est monté dans le `label` du Chip (et non dans `icon`)
  // pour garder la maîtrise de la taille du point / de la coche.
  statusChipLabel: {
    display: "flex",
    alignItems: "center",
  },

  statusChipIcon: {
    fontSize: 14,
    marginRight: theme.spacing(0.75),
  },

  // Un box-shadow inset ne s'affiche pas sur un <tr> en border-collapse :
  // la ligne en cours est donc teintée plutôt que bordée.
  liveRow: {
    backgroundColor: "#FFFCF2",
    "&:hover": {
      backgroundColor: "#FFF7E3",
    },
  },

  // ---------------------------------------------------------------------------
  // Détail d'une session — en-tête
  // ---------------------------------------------------------------------------
  detailHeader: {
    padding: theme.spacing(2.5, 3),
    backgroundColor: SURFACE,
    borderRadius: 12,
    border: `1px solid ${BORDER}`,
    marginBottom: theme.spacing(2),
  },

  detailTitleRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
  },

  detailTeamName: {
    fontWeight: 700,
    color: INK,
    fontSize: "1.5rem",
    marginRight: theme.spacing(1.5),
    wordBreak: "break-word",
    [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
  },

  detailMeta: {
    display: "flex",
    flexWrap: "wrap",
    marginTop: theme.spacing(1.5),
  },

  detailMetaItem: {
    display: "flex",
    alignItems: "center",
    marginRight: theme.spacing(3),
    marginTop: theme.spacing(0.5),
    color: INK_MUTED,
    fontSize: "0.875rem",
    minWidth: 0,
  },

  detailMetaIcon: {
    fontSize: 18,
    marginRight: theme.spacing(0.75),
    color: INK_MUTED,
    flexShrink: 0,
  },

  detailMetaValue: {
    color: INK,
    fontWeight: 500,
    wordBreak: "break-word",
  },

  // ---------------------------------------------------------------------------
  // Détail d'une session — sections
  // ---------------------------------------------------------------------------
  detailSection: {
    backgroundColor: SURFACE,
    borderRadius: 12,
    border: `1px solid ${BORDER}`,
    padding: theme.spacing(2.5),
    height: "100%",
    boxSizing: "border-box",
  },

  detailSectionTitle: {
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontSize: "0.75rem",
    fontWeight: 700,
    color: INK_MUTED,
    marginBottom: theme.spacing(2),
  },

  // Chiffre phare : un seul par vue (les points gagnés).
  heroValue: {
    fontWeight: 700,
    color: NAVY,
    fontSize: "3rem",
    lineHeight: 1.1,
    [theme.breakpoints.down("xs")]: { fontSize: "2.25rem" },
  },

  heroUnit: {
    fontWeight: 600,
    color: INK_MUTED,
    fontSize: "1.125rem",
    marginLeft: theme.spacing(0.5),
  },

  heroLabel: {
    color: INK_MUTED,
    fontSize: "0.875rem",
    marginBottom: theme.spacing(0.5),
  },

  // ---------------------------------------------------------------------------
  // Jauges (réalisé / total)
  // ---------------------------------------------------------------------------
  meterRow: {
    marginTop: theme.spacing(2),
  },

  meterHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: theme.spacing(0.75),
  },

  meterLabel: {
    color: INK_MUTED,
    fontSize: "0.8125rem",
  },

  meterValue: {
    color: INK,
    fontWeight: 600,
    fontSize: "0.875rem",
    fontVariantNumeric: "tabular-nums",
  },

  meterTrack: {
    height: 8,
    borderRadius: 4,
    backgroundColor: NAVY_TRACK,
    overflow: "hidden",
  },

  meterFill: {
    height: "100%",
    borderRadius: 4,
    backgroundColor: NAVY,
    transition: "width 0.4s ease",
  },

  // ---------------------------------------------------------------------------
  // Tuiles de statistiques
  // ---------------------------------------------------------------------------
  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
    gridGap: "12px",
  },

  statTile: {
    backgroundColor: SURFACE_ALT,
    border: `1px solid ${BORDER}`,
    borderRadius: 10,
    padding: theme.spacing(1.5),
    minWidth: 0,
  },

  statLabel: {
    color: INK_MUTED,
    fontSize: "0.75rem",
    lineHeight: 1.3,
    marginBottom: theme.spacing(0.5),
  },

  statValue: {
    color: INK,
    fontWeight: 600,
    fontSize: "1.25rem",
    lineHeight: 1.2,
  },

  statValuePending: {
    color: INK_MUTED,
    fontWeight: 500,
  },

  statUnit: {
    color: INK_MUTED,
    fontSize: "0.8125rem",
    fontWeight: 500,
    marginLeft: 2,
  },

  // ---------------------------------------------------------------------------
  // Photos de la session
  // ---------------------------------------------------------------------------
  photoFrame: {
    position: "relative",
    backgroundColor: SURFACE_ALT,
    border: `1px solid ${BORDER}`,
    borderRadius: 10,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 260,
  },

  photoImage: {
    display: "block",
    width: "100%",
    maxHeight: 420,
    objectFit: "contain",
    cursor: "zoom-in",
  },

  photoOverlayButton: {
    position: "absolute",
    backgroundColor: "rgba(21, 27, 47, 0.72)",
    color: SURFACE,
    "&:hover": { backgroundColor: "rgba(21, 27, 47, 0.9)" },
    "&.Mui-focusVisible": { outline: `2px solid ${SURFACE}`, outlineOffset: 2 },
  },

  photoZoomButton: {
    top: theme.spacing(1),
    right: theme.spacing(1),
  },

  photoDeleteButton: {
    bottom: theme.spacing(1),
    right: theme.spacing(1),
    backgroundColor: "rgba(198, 40, 40, 0.85)",
    "&:hover": { backgroundColor: "rgba(198, 40, 40, 1)" },
  },

  photoNavButton: {
    top: "50%",
    transform: "translateY(-50%)",
  },

  photoEnigma: {
    marginTop: theme.spacing(1.5),
  },

  photoEnigmaTitle: {
    fontWeight: 600,
    color: INK,
    fontSize: "0.9375rem",
    wordBreak: "break-word",
  },

  photoEnigmaDescription: {
    color: INK_MUTED,
    fontSize: "0.8125rem",
    marginTop: theme.spacing(0.5),
    whiteSpace: "pre-line",
  },

  thumbStrip: {
    display: "flex",
    flexWrap: "wrap",
    marginTop: theme.spacing(1.5),
    marginLeft: theme.spacing(-0.5),
  },

  thumb: {
    width: 56,
    height: 56,
    borderRadius: 8,
    objectFit: "cover",
    cursor: "pointer",
    margin: theme.spacing(0.5),
    border: `2px solid transparent`,
    opacity: 0.65,
    transition: "opacity 0.2s ease, border-color 0.2s ease",
    "&:hover": { opacity: 1 },
  },

  thumbActive: {
    borderColor: NAVY,
    opacity: 1,
  },

  photoCounter: {
    color: INK_MUTED,
    fontSize: "0.8125rem",
    fontVariantNumeric: "tabular-nums",
  },

  photoToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1.5),
  },

  downloadButton: {
    marginTop: theme.spacing(1.5),
    textTransform: "none",
    fontWeight: 600,
    backgroundColor: NAVY,
    color: SURFACE,
    "&:hover": { backgroundColor: "#1a2136" },
  },

  emptyPhotos: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: theme.spacing(4, 2),
    color: INK_MUTED,
    backgroundColor: SURFACE_ALT,
    border: `1px dashed ${BORDER}`,
    borderRadius: 10,
  },

  emptyPhotosIcon: {
    fontSize: 40,
    color: "#B6BDCB",
    marginBottom: theme.spacing(1),
  },

  detailContent: {
    padding: theme.spacing(2.5),
    backgroundColor: SURFACE_ALT,
  },
});

export default styles;
